import { useState } from "react";

import { cn } from "../components/ui/cn";
import type { User } from "../types";

import { AgentSettings } from "./settings/AgentSettings";
import { ChannelSettings } from "./settings/ChannelSettings";
import { HolidaySettings } from "./settings/HolidaySettings";
import { ImageSettings } from "./settings/ImageSettings";
import { LaneSettings } from "./settings/LaneSettings";
import { PipelineSettings } from "./settings/PipelineSettings";
import { SendFromSettings } from "./settings/SendFromSettings";
import { SignatureSettings } from "./settings/SignatureSettings";
import { SpamSettings } from "./settings/SpamSettings";
import { TeamSettings } from "./settings/TeamSettings";
import { CrmSettingsSections } from "./crm/CrmSettings";
import { SalesSettingsSections } from "./settings/SalesSettings";

/**
 * One settings page for the whole platform.
 *
 * It used to be two, one reached from inside the Helpdesk and one from inside
 * the CRM, and that split made a promise the data never kept: **a person's role
 * is not per product.** Somebody is a manager of Pulse, not a manager of the
 * Helpdesk who happens also to appear in the CRM — there is one `users` table
 * and one role column behind both. Two settings pages implied two sets of
 * permissions, and the first question anybody asked was which one to change.
 *
 * So the page is grouped by *what a setting is about* rather than by which
 * product you happened to be looking at when you needed it:
 *
 *   - **People** is shared, and is the group that makes the point. Users and
 *     teams apply everywhere.
 *   - **Helpdesk** and **CRM** hold the settings that genuinely belong to one
 *     product — channels and pipelines cannot mean anything to the CRM, and
 *     company custom fields cannot mean anything to a channel.
 *
 * Reached from the cog on the app rail rather than from either product's
 * sidebar, for the same reason: the rail is the platform, and this now belongs
 * to the platform.
 */

type Group = "people" | "helpdesk" | "crm" | "sales";

/*
 * Just the four names. Each tab used to carry a line summarising what was
 * inside it, which described the panel titles sitting directly underneath --
 * and the header above carried a paragraph explaining that settings are
 * settings. Both were read once and skipped forever after.
 */
const GROUPS: { id: Group; label: string }[] = [
  { id: "people", label: "People" },
  { id: "helpdesk", label: "Helpdesk" },
  { id: "crm", label: "CRM" },
  { id: "sales", label: "Sales calls" },
];

export function Settings({ user }: { user: User }) {
  const isAdmin = user.role === "admin";
  const [group, setGroup] = useState<Group>("people");

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden p-4 sm:p-6">
      {/*
        For a screen reader only on a phone, where the pink bar above already
        says "Settings" and a second heading saying it again cost a line of a
        page that is mostly scrolling.
      */}
      <header className="mb-4 max-md:sr-only">
        <h1 className="text-lg font-semibold text-content">Settings</h1>
      </header>

      {/*
        Tabs rather than one long scroll. Merging two pages into one made a page
        long enough that the thing you came for was always somewhere below the
        fold, which would have traded one kind of confusion for another.
      */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-line">
        {GROUPS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setGroup(entry.id)}
            aria-current={group === entry.id ? "page" : undefined}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm",
              group === entry.id
                ? "border-brand-600 font-medium text-content"
                : "border-transparent text-muted hover:text-brand-600 dark:hover:text-brand-400",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-8">
        {group === "people" ? (
          <>
            {/*
              First, and shared, because this is where the one-role-everywhere
              point is made. A manager here is a manager of both products.
            */}
            {isAdmin ? <AgentSettings /> : null}
            <TeamSettings isAdmin={isAdmin} />
          </>
        ) : null}

        {group === "helpdesk" ? (
          <>
            {isAdmin ? <ChannelSettings /> : null}
            <PipelineSettings isAdmin={isAdmin} />
            {isAdmin ? <SendFromSettings /> : null}
            {isAdmin ? <LaneSettings /> : null}
            {isAdmin ? <ImageSettings /> : null}
            {isAdmin ? <SignatureSettings /> : null}
            {/* Readable by everybody, unlike the panels above it: an agent
                looking at something in the Spam view wants to know whether the
                sender is already allowed. */}
            <SpamSettings isAdmin={isAdmin} />
            <HolidaySettings isAdmin={isAdmin} />
          </>
        ) : null}

        {group === "crm" ? <CrmSettingsSections isAdmin={isAdmin} /> : null}

        {group === "sales" ? <SalesSettingsSections isAdmin={isAdmin} /> : null}
      </div>
    </div>
  );
}
